import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { Toast } from 'radix-ui'
import { Icon, type IconName } from '@/components/ui/Icon'
import { cn } from '@/lib/cn'
import {
  ToastContext,
  type ToastMessage,
  type ToastRequest,
  type ToastTone,
} from '@/components/ui/toast-context'

const toneClasses: Record<ToastTone, string> = {
  success: 'border-border-brand text-success-strong',
  error: 'border-error/40 text-error-strong',
  info: 'border-info/30 text-info-strong',
}

const toneIcons: Record<ToastTone, IconName> = {
  success: 'check',
  error: 'alertTriangle',
  info: 'info',
}

type ToastProviderProps = {
  children: ReactNode
  duration?: number
}

export function ToastProvider({ children, duration = 5000 }: ToastProviderProps) {
  const [messages, setMessages] = useState<ToastMessage[]>([])
  const counter = useRef(0)

  const notify = useCallback((request: ToastRequest) => {
    counter.current += 1
    const id = `toast-${String(counter.current)}`
    setMessages((current) => [...current, { ...request, id }])
  }, [])

  const dismiss = useCallback((id: string) => {
    setMessages((current) => current.filter((message) => message.id !== id))
  }, [])

  const api = useMemo(() => ({ notify }), [notify])

  return (
    <ToastContext.Provider value={api}>
      <Toast.Provider swipeDirection="right" duration={duration}>
        {children}

        {messages.map((message) => {
          const tone = message.tone ?? 'info'

          return (
            <Toast.Root
              key={message.id}
              onOpenChange={(open) => {
                if (!open) {
                  dismiss(message.id)
                }
              }}
              className={cn(
                'flex items-start gap-3 rounded-lg border bg-surface p-4 shadow-md',
                toneClasses[tone],
              )}
            >
              <Icon name={toneIcons[tone]} className="mt-0.5 size-4" />

              <div className="flex min-w-0 flex-col gap-1">
                <Toast.Title className="text-sm font-semibold text-ink">
                  {message.title}
                </Toast.Title>
                {message.description === undefined ? null : (
                  <Toast.Description className="text-sm text-ink-muted">
                    {message.description}
                  </Toast.Description>
                )}
              </div>

              <Toast.Close
                aria-label="Close"
                className={cn(
                  'ml-auto inline-flex size-6 shrink-0 items-center justify-center rounded-md text-ink-muted',
                  'transition-colors duration-250 ease-brand hover:bg-surface-hover hover:text-ink',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                )}
              >
                <Icon name="close" className="size-4" />
              </Toast.Close>
            </Toast.Root>
          )
        })}

        <Toast.Viewport
          className={cn(
            'fixed right-4 bottom-4 z-50 flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-2',
            'outline-none',
          )}
        />
      </Toast.Provider>
    </ToastContext.Provider>
  )
}
